import { createSlice } from '@reduxjs/toolkit';
import {
  handleSetSelectedFilterIds,
  handleSetSelectedContentBasedValues,
  handleSetSelectedFilterValue,
} from 'store/helpers/filterHelpers';
import {
  instagramInitialState,
  telegramInitialState,
  twitterInitialState,
  exactSearchInitialState,
} from './initialState';
import {
  addVipResource,
  fetchSocialAccountInfoByUsername,
  fetchVipPlatformInfo,
  fetchVipResources,
  removeVipResource,
} from './vipResources.actions';
import { adaptVipCapacities } from './vipResources.adapter';

const filtersInitialState = {
  instagram: instagramInitialState,
  telegram: telegramInitialState,
  twitter: twitterInitialState,
};

export const vipResourceInitialState = {
  filters: filtersInitialState,
  selectedPlatformForResources: 'instagram',
  exactSearch: exactSearchInitialState,
  vipResources: {
    twitter: {
      data: [],
      loading: false,
      error: null,
    },
    instagram: {
      data: [],
      loading: false,
      error: null,
    },
    telegram: {
      data: [],
      loading: false,
      error: null,
    },
  },
  vipInfo: {
    data: {},
    capacities: {},
    loading: false,
    error: null,
  },
};

const bumpCapacityUsed = (state, platform, delta) => {
  const capacity = state.vipInfo.capacities[platform];
  if (!capacity || !Number.isFinite(capacity.used)) return;
  capacity.used = Math.max(0, capacity.used + delta);
};

export const vipResourcesSlice = createSlice({
  name: 'vipResourcesSlice',
  initialState: vipResourceInitialState,
  reducers: {
    setSelectedFilterIds: (state, action) => {
      handleSetSelectedFilterIds(state, action);
    },
    setSelectedContentBasedValues: (state, action) => {
      handleSetSelectedContentBasedValues(state, action);
    },
    setSelectedFilterValue: (state, action) => {
      handleSetSelectedFilterValue(state, action, filtersInitialState);
    },
    changeSortBy: (state, action) => {
      const { componentName, parentPlatform, order, selected } = action.payload;
      const sort = state.filters[parentPlatform][componentName];
      if (order) {
        sort.order = order;
      }
      if (selected) {
        sort.selected = selected;
      }
    },
    resetAllFilters: (state) => {
      state.filters = filtersInitialState;
    },
    setSelectedPlatformForResources: (state, action) => {
      state.selectedPlatformForResources = action.payload;
    },
    toggleFilterActivateSwitch: (state, action) => {
      const { componentName } = action.payload;
      const filter =
        state.filters[state.selectedPlatformForResources][componentName];
      filter.isDisabled = !filter.isDisabled;
    },
    changeExactSearchValue: (state, action) => {
      const { value, key } = action.payload;
      state.exactSearch[key] = value;

      // اگر مودال رو خواستیم باز کنیم باید برگردیم به یکسری مقادیر پیش فرض
      if (key === 'isModalOpen' && value) {
        state.exactSearch.username = exactSearchInitialState.username;
        state.exactSearch.platform = exactSearchInitialState.platform;
        state.exactSearch.socialAccount = exactSearchInitialState.socialAccount;
      }
    },
    removeSelectedVipResource: (state, action) => {
      const { platform, vipResourceId } = action.payload;
      const bucket = state.vipResources[platform];
      if (!bucket?.data) return;
      const vipResourceIndex = bucket.data.findIndex(
        (vipResource) =>
          vipResource.vipId === vipResourceId ||
          vipResource.id === vipResourceId ||
          vipResource.resource_id === vipResourceId
      );
      if (vipResourceIndex < 0) return;
      bucket.data.splice(vipResourceIndex, 1);
    },
    removeAllSelectedVipResources: (state, action) => {
      const { platform, vipResourceIds } = action.payload;
      state.vipResources[platform].data = state.vipResources[
        platform
      ].data?.filter(
        (vipResource) => !vipResourceIds?.includes(vipResource.resource_id)
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVipResources.pending, (state, action) => {
        const { platform } = action.meta.arg;
        if (!state.vipResources[platform]) {
          state.vipResources[platform] = {
            data: [],
            loading: false,
            error: null,
          };
        }
        state.vipResources[platform].loading = true;
        state.vipResources[platform].error = null;
      })
      .addCase(fetchVipResources.fulfilled, (state, action) => {
        const { platform } = action.meta.arg;
        const { vipResources } = action.payload;
        state.vipResources[platform].data = vipResources;
        state.vipResources[platform].loading = false;
        state.vipResources[platform].error = null;
      })
      .addCase(fetchVipResources.rejected, (state, action) => {
        const { platform } = action.meta.arg;
        state.vipResources[platform].loading = false;
        state.vipResources[platform].error = action.payload;
      })
      .addCase(addVipResource.fulfilled, (state, action) => {
        const { platform } = action.meta.arg;
        const { vipResource } = action.payload;
        if (vipResource) {
          state.vipResources[platform].data.unshift(vipResource);
        }
        bumpCapacityUsed(state, platform, 1);
      })
      .addCase(removeVipResource.fulfilled, (state, action) => {
        const { platform } = action.meta.arg;
        bumpCapacityUsed(state, platform, -1);
      })
      .addCase(fetchSocialAccountInfoByUsername.fulfilled, (state, action) => {
        state.exactSearch.socialAccount.data = action.payload;
        state.exactSearch.socialAccount.loading = false;
        state.exactSearch.socialAccount.error = null;
        state.exactSearch.isModalOpen = false;
      })
      .addCase(fetchSocialAccountInfoByUsername.pending, (state) => {
        state.exactSearch.socialAccount.loading = true;
        state.exactSearch.socialAccount.error = null;
      })
      .addCase(fetchSocialAccountInfoByUsername.rejected, (state, action) => {
        state.exactSearch.socialAccount.loading = false;
        state.exactSearch.socialAccount.error = action.payload;
      })
      .addCase(fetchVipPlatformInfo.fulfilled, (state, action) => {
        state.vipInfo.data = action.payload ?? [];
        state.vipInfo.capacities = adaptVipCapacities(action.payload);
        state.vipInfo.loading = false;
        state.vipInfo.error = null;
      })
      .addCase(fetchVipPlatformInfo.pending, (state) => {
        if (!state.vipInfo) {
          state.vipInfo = {
            data: {},
            capacities: {},
            loading: false,
            error: null,
          };
        }
        state.vipInfo.loading = true;
        state.vipInfo.error = null;
      })
      .addCase(fetchVipPlatformInfo.rejected, (state, action) => {
        state.vipInfo.loading = false;
        state.vipInfo.error = action.payload;
      });
  },
});

export const {
  setSelectedPlatformForResources,
  setSelectedFilterIds,
  resetAllFilters,
  setSelectedContentBasedValues,
  changeSortBy,
  toggleFilterActivateSwitch,
  changeExactSearchValue,
  removeSelectedVipResource,
  removeAllSelectedVipResources,
} = vipResourcesSlice.actions;
